import { type ChangeEvent, useCallback } from 'react'
import { MenuItem, Stack } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { DateTime } from 'luxon'
import SelectV2 from '../../Common/Form/SelectV2'

export type AccountDateRangePreset = 'all' | 'today' | 'last-7-days' | 'last-30-days' | 'this-month' | 'last-month' | 'custom'

export type AccountDateRangeValue = {
  preset: AccountDateRangePreset
  dateFrom: string
  dateTo: string
}

type AccountDateRangeOption = {
  value: AccountDateRangePreset
  label: string
}

type Props = {
  label: string
  value: AccountDateRangeValue
  options: AccountDateRangeOption[]
  fromLabel: string
  toLabel: string
  onChange: (value: AccountDateRangeValue) => void
}

const formatDateInput = (date: DateTime) => date.toFormat('yyyy-MM-dd')

const parseDateInput = (date: string) => {
  if (!date) {
    return null
  }

  const dateTime = DateTime.fromFormat(date, 'yyyy-MM-dd')
  return dateTime.isValid ? dateTime : null
}

export const getAccountDateRangePresetValue = (preset: AccountDateRangePreset): AccountDateRangeValue => {
  const today = DateTime.local()

  if (preset === 'all') {
    return {
      preset,
      dateFrom: '',
      dateTo: '',
    }
  }

  if (preset === 'today') {
    return {
      preset,
      dateFrom: formatDateInput(today),
      dateTo: formatDateInput(today),
    }
  }

  if (preset === 'last-7-days') {
    return {
      preset,
      dateFrom: formatDateInput(today.minus({ days: 6 })),
      dateTo: formatDateInput(today),
    }
  }

  if (preset === 'last-30-days') {
    return {
      preset,
      dateFrom: formatDateInput(today.minus({ days: 29 })),
      dateTo: formatDateInput(today),
    }
  }

  if (preset === 'this-month') {
    return {
      preset,
      dateFrom: formatDateInput(today.startOf('month')),
      dateTo: formatDateInput(today),
    }
  }

  if (preset === 'last-month') {
    const lastMonth = today.minus({ months: 1 })

    return {
      preset,
      dateFrom: formatDateInput(lastMonth.startOf('month')),
      dateTo: formatDateInput(lastMonth.endOf('month')),
    }
  }

  return {
    preset,
    dateFrom: '',
    dateTo: '',
  }
}

export default function AccountDateRangeFilter(props: Props) {
  const onChangePreset = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const preset = e.target.value as AccountDateRangePreset

    if (preset === props.value.preset) {
      return
    }

    if (preset === 'custom') {
      props.onChange({
        preset,
        dateFrom: props.value.dateFrom,
        dateTo: props.value.dateTo,
      })
      return
    }

    props.onChange(getAccountDateRangePresetValue(preset))
  }, [props])

  const onChangeDateFrom = useCallback((date: DateTime | null) => {
    props.onChange({
      ...props.value,
      preset: 'custom',
      dateFrom: date?.isValid ? formatDateInput(date) : '',
    })
  }, [props])

  const onChangeDateTo = useCallback((date: DateTime | null) => {
    props.onChange({
      ...props.value,
      preset: 'custom',
      dateTo: date?.isValid ? formatDateInput(date) : '',
    })
  }, [props])

  return <Stack spacing={1.25}>
    <SelectV2
      label={props.label}
      value={props.value.preset}
      onChange={onChangePreset}
    >
      {props.options.map(option => (
        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
      ))}
    </SelectV2>
    {props.value.preset === 'custom' ? (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <DatePicker
          label={props.fromLabel}
          value={parseDateInput(props.value.dateFrom)}
          onChange={onChangeDateFrom}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
            },
          }}
        />
        <DatePicker
          label={props.toLabel}
          value={parseDateInput(props.value.dateTo)}
          onChange={onChangeDateTo}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
            },
          }}
        />
      </Stack>
    ) : null}
  </Stack>
}
